const STATUS_STYLE = {
    draft: "text-amber-700 bg-amber-50",
    submitted: "text-emerald-700 bg-emerald-50",
    updated: "text-violet-700 bg-violet-50",
    open: "text-sky-700 bg-sky-50",
    "in progress": "text-violet-700 bg-violet-50",
    done: "text-emerald-700 bg-emerald-50",
};

const PRIORITY_STYLE = {
    low: "text-neutral-600 bg-neutral-100",
    medium: "text-sky-700 bg-sky-50",
    high: "text-amber-700 bg-amber-50",
    urgent: "text-rose-700 bg-rose-50",
};

export function StatusPill({ status }) {
    const key = String(status || "").toLowerCase();
    const cls = STATUS_STYLE[key] || "text-neutral-600 bg-neutral-100";
    return (
        <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.12em] uppercase rounded-full px-2 py-0.5 ${cls}`}>
            {status || "—"}
        </span>
    );
}

export function PriorityPill({ priority }) {
    const key = String(priority || "").toLowerCase();
    const cls = PRIORITY_STYLE[key] || "text-neutral-600 bg-neutral-100";
    return (
        <span className={`inline-flex items-center text-[10px] font-semibold tracking-[0.12em] uppercase rounded-full px-2 py-0.5 ${cls}`}>
            {priority || "—"}
        </span>
    );
}

export function PageHeader({ eyebrow, title, subtitle, actions }) {
    return (
        <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-5 mb-8">
            <div className="max-w-3xl">
                {eyebrow && (
                    <div className="inline-flex items-center gap-3 text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-500">
                        <span className="h-px w-6 bg-neutral-300" />
                        {eyebrow}
                    </div>
                )}
                <h1 className="mt-3 font-display text-3xl sm:text-4xl lg:text-5xl font-light tracking-tight text-black">{title}</h1>
                {subtitle && <p className="mt-3 text-[14px] sm:text-[15px] text-neutral-600 leading-relaxed">{subtitle}</p>}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </header>
    );
}

export function ComingSoonGrid({ items }) {
    return (
        <div className="mt-10">
            <div className="text-[11px] font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-3">Coming soon</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {items.map((c, i) => (
                    <div key={i} data-testid={`coming-soon-${i}`} className="relative glass rounded-2xl p-5 overflow-hidden">
                        <div className="absolute inset-0 pointer-events-none opacity-70" style={{ background: "radial-gradient(120% 100% at 100% 0%, rgba(139,92,246,0.08), transparent 60%)" }} />
                        <div className="relative">
                            <div className="flex items-center justify-between">
                                <span className="font-display text-[16px] font-medium text-neutral-500">{c.title}</span>
                                <span className="inline-flex items-center gap-1 text-[10px] font-semibold tracking-[0.16em] uppercase text-violet-700 bg-violet-50 rounded-full px-2 py-0.5">
                                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><rect x="4" y="10" width="16" height="10" rx="2" stroke="currentColor" strokeWidth="1.6" /><path d="M8 10V7a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.6" /></svg>
                                    Soon
                                </span>
                            </div>
                            <p className="mt-2 text-[12.5px] text-neutral-400 leading-relaxed">{c.body}</p>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}

export function ActionIcon({ name }) {
    const paths = {
        target: <><circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="1.6" /><circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="1.6" /></>,
        handshake: <path d="M4 12l4-4 3 3 3-3 6 6-4 4-5-5-3 3-4-4z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        chart: <path d="M4 20V10M10 20V4M16 20v-8M22 20H2" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />,
        receipt: <path d="M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />,
        trending: <path d="M3 17l6-6 4 4 8-8M21 7h-5m5 0v5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />,
        wallet: <path d="M3 7a2 2 0 012-2h12v4H5a2 2 0 01-2-2zm0 0v10a2 2 0 002 2h14a2 2 0 002-2V9H7m10 3h.01" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />,
        user: <><circle cx="12" cy="8" r="4" stroke="currentColor" strokeWidth="1.6" /><path d="M4 20a8 8 0 0116 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></>,
        calendar: <><rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.5" /><path d="M3 9h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" /></>,
        star: <path d="M12 3l2.7 5.5 6 .9-4.3 4.2 1 6L12 17l-5.4 2.8 1-6L3.3 9.4l6-.9L12 3z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />,
        box: <path d="M3 7l9-4 9 4-9 4-9-4zm0 0v10l9 4 9-4V7M12 11v10" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        alert: <><path d="M12 3l9 16H3L12 3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" /><path d="M12 9v5M12 17h.01" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></>,
        megaphone: <path d="M3 11v2l4 1 3 5 2-1-2-4 9-3V6L7 9 3 11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        note: <path d="M5 4h14v12l-5 5H5V4zM14 21v-5h5" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        ticket: <path d="M4 7a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 000 4v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a2 2 0 000-4V7z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        cart: <path d="M3 4h2l2.5 12h11L21 8H6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />,
        file: <path d="M14 3H6a2 2 0 00-2 2v14a2 2 0 002 2h12a2 2 0 002-2V9l-6-6zM14 3v6h6" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        shield: <path d="M12 3l8 3v6c0 4.5-3.5 8-8 9-4.5-1-8-4.5-8-9V6l8-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />,
        refresh: <path d="M4 12a8 8 0 0113-6l3 2M20 12a8 8 0 01-13 6l-3-2M17 4v4h-4M7 20v-4h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />,
        globe: <><circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.5" /><path d="M3 12h18M12 3c3 3 3 15 0 18M12 3c-3 3-3 15 0 18" stroke="currentColor" strokeWidth="1.3" /></>,
        search: <><circle cx="11" cy="11" r="6" stroke="currentColor" strokeWidth="1.6" /><path d="M20 20l-3.5-3.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" /></>,
        flag: <path d="M5 21V4m0 0h11l-2 4 2 4H5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />,
        check: <path d="M20 6L9 17l-5-5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />,
    };
    return (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none">{paths[name] || paths.note}</svg>
    );
}
